/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback, useEffect, useMemo } from 'react'
// Third-party
import { css } from '@emotion/react'
import { CloseOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
// Relative
import {
  heading4,
  paragraphSmall,
  paragraphXSmall,
  paragraphMedium,
  weightSemiBold,
  weightMedium,
  weightRegular,
  weightBold,
} from 'src/components/css'
import {
  CheckboxOutlineIcon,
  CheckboxCheckedIcon,
  CheckboxDisabledIcon,
} from './icons'

export type DivisionScheduleStatus =
  | 'pending'
  | 'draft'
  | 'approved'
  | 'applied'

export interface DivisionLocation {
  id: string
  name: string
  surfaceCount: number
}

export interface Division {
  id: string
  name: string
  locations: DivisionLocation[] | 'all'
  status: string
  scheduleStatus?: DivisionScheduleStatus
  isDisabled?: boolean
  isApproved?: boolean
  lastUpdatedAt?: string
  lastUpdatedBy?: string
}

interface GenerateScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (
    selectedDivisions: string[],
    selectedLocations: string[],
    divisionPriority: string[]
  ) => void
  onResetDivisions?: (divisionIds: string[]) => Promise<boolean>
  divisions: Division[]
  previewPayload?: Record<string, unknown> | null
  isLoadingPreview?: boolean
  divisionPriorityOrder?: string[]
}

/**
 * Status badge for division schedule status
 */
const StatusBadge: React.FC<{
  status?: DivisionScheduleStatus
  isApproved?: boolean
}> = ({ status, isApproved }) => {
  let bgColor: string
  let textColor: string
  let borderColor: string
  let label: string

  if (isApproved) {
    bgColor = `${colors.SUCCESS_300}33`
    textColor = colors.SUCCESS_300
    borderColor = `${colors.SUCCESS_300}60`
    label = 'Published'
  } else {
    switch (status) {
      case 'applied':
      case 'approved':
        bgColor = `${colors.SUCCESS_300}33`
        textColor = colors.SUCCESS_300
        borderColor = `${colors.SUCCESS_300}60`
        label = 'Published'
        break
      case 'draft':
        bgColor = colors.NEUTRAL_700
        textColor = colors.NEUTRAL_50
        borderColor = colors.NEUTRAL_100
        label = 'Draft'
        break
      case 'pending':
      default:
        bgColor = `${colors.WARNING_300}20`
        textColor = colors.WARNING_300
        borderColor = `${colors.WARNING_300}60`
        label = 'Pending'
        break
    }
  }

  return (
    <span
      css={css`
        display: inline-block;
        padding: 2px 4px;
        border-radius: 4px;
        background: ${bgColor};
        color: ${textColor};
        border: 1px solid ${borderColor};
        font-family: 'Inter', sans-serif;
        font-size: 12px;
        font-weight: 400;
        white-space: nowrap;
        text-align: center;
      `}
    >
      {label}
    </span>
  )
}

const modalStyles = css`
  .ant-modal-content {
    border-radius: 8px;
    background: ${colors.SECONDARY_300};
    border: 1px solid ${colors.SECONDARY_100};
    padding: 0;
    overflow: hidden;
  }
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-header {
    display: none;
  }
  .ant-modal-close {
    display: none;
  }
`

const headerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid ${colors.SECONDARY_100};
`

const titleStyles = css`
  ${heading4}
  ${weightSemiBold}
  color: ${colors.WHITE};
  margin: 0;
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
`

const closeButtonStyles = css`
  background: transparent;
  border: none;
  color: ${colors.WHITE};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`

const formStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

const sectionHeaderStyles = css`
  display: flex;
  flex-direction: column;
`

const sectionTitleStyles = css`
  ${paragraphMedium}
  ${weightBold}
  color: ${colors.WHITE};
  margin: 0;
`

const sectionSubtitleStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  margin: 0;
`

const readyDisclaimerStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  margin: 8px 0 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background-color: ${colors.SECONDARY_200};
  border-radius: 6px;
  padding: 8px 12px;

  i {
    color: ${colors.INFO_300};
    margin-top: 2px;
  }
`

const tableContainerStyles = css`
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const tableHeaderStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 16px;
  background-color: ${colors.SECONDARY_100};
`

const tableHeaderCellStyles = css`
  ${paragraphXSmall}
  ${weightMedium}
  color: ${colors.NEUTRAL_70};
  flex: 1;
  text-transform: uppercase;
`

const divisionRowStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  background-color: ${colors.SECONDARY_500};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${colors.SECONDARY_400};
  }
`

const divisionRowSelectedStyles = css`
  background-color: rgba(56, 148, 255, 0.08);

  &:hover {
    background-color: rgba(56, 148, 255, 0.12);
  }
`

const divisionRowDisabledStyles = css`
  background-color: ${colors.SECONDARY_500};
  cursor: default;

  &:hover {
    background-color: ${colors.SECONDARY_500};
  }
`

const altRowStyles = css`
  background-color: ${colors.SECONDARY_400};

  &:hover {
    background-color: ${colors.SECONDARY_300};
  }
`

const divisionNameStyles = css`
  ${paragraphSmall}
  ${weightRegular}
  color: ${colors.WHITE};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const locationCellStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const statusCellStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const footerStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px;
  border-top: 1px solid ${colors.SECONDARY_100};
`

const buttonContainerStyles = css`
  display: flex;
  gap: 16px;
  width: 360px;
`

const cancelButtonStyles = css`
  flex: 1;
  padding: 9px 25px;
  border-radius: 8px;
  border: 1px solid ${colors.WHITE};
  background: transparent;
  color: ${colors.WHITE};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`

const confirmButtonStyles = css`
  flex: 1;
  padding: 8px 24px;
  border-radius: 8px;
  border: none;
  background: ${colors.PRIMARY_300};
  color: ${colors.WHITE};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.PRIMARY_400};
  }

  &:disabled {
    background: ${colors.NEUTRAL_100};
    cursor: not-allowed;
  }
`

const checkboxContainerStyles = css`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease;
`

const checkboxCheckedContainerStyles = css`
  transform: scale(1.15);
  animation: checkScale 0.25s ease-out;

  @keyframes checkScale {
    0% {
      transform: scale(0.8);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1.15);
    }
  }
`

const confirmationContainerStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px 16px;
  text-align: center;
`

const warningIconStyles = css`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${colors.WARNING_300}20;
  display: flex;
  align-items: center;
  justify-content: center;
`

const confirmationHeadingStyles = css`
  ${paragraphMedium}
  ${weightBold}
  color: ${colors.WHITE};
  margin: 0;
`

const confirmationListStyles = css`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const confirmationListItemStyles = css`
  ${paragraphSmall}
  ${weightMedium}
  color: ${colors.NEUTRAL_50};
`

const confirmationNoteStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_70};
  margin: 0;
`

const regenerateButtonStyles = css`
  flex: 1;
  padding: 8px 24px;
  border-radius: 8px;
  border: none;
  background: ${colors.WARNING_300};
  color: ${colors.WHITE};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.WARNING_400};
  }
`

const GenerateScheduleModal: React.FC<GenerateScheduleModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  divisions,
  previewPayload = null,
  isLoadingPreview = false,
  divisionPriorityOrder = [],
}) => {
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([])
  const [selectedLocations, setSelectedLocations] = useState<string[]>([])
  const [copied, setCopied] = useState(false)
  const [confirmStep, setConfirmStep] = useState(false)

  // Pre-select divisions with status 'generated' or 'approved' when modal opens
  useEffect(() => {
    if (isOpen) {
      const preSelected = divisions
        .filter(
          (d) =>
            !d.isDisabled &&
            !d.isApproved &&
            (d.scheduleStatus === 'draft' ||
              d.scheduleStatus === 'approved' ||
              d.scheduleStatus === 'applied')
        )
        .map((d) => d.id)
      setSelectedDivisions(preSelected)
    }
  }, [isOpen, divisions])

  // Divisions sorted by sidebar priority order
  const orderedDivisions = useMemo(() => {
    if (divisionPriorityOrder.length === 0) {
      return divisions
    }
    const orderMap = new Map(divisionPriorityOrder.map((id, idx) => [id, idx]))
    return [...divisions].sort((a, b) => {
      const aIdx = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER
      const bIdx = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER
      return aIdx - bIdx
    })
  }, [divisions, divisionPriorityOrder])

  const divisionsWithGames = useMemo(() => {
    const filtered = divisions.filter(
      (d) =>
        selectedDivisions.includes(d.id) &&
        (d.scheduleStatus === 'draft' ||
          d.scheduleStatus === 'approved' ||
          d.scheduleStatus === 'applied')
    )
    if (divisionPriorityOrder.length === 0) {
      return filtered
    }
    const orderMap = new Map(divisionPriorityOrder.map((id, idx) => [id, idx]))
    return [...filtered].sort(
      (a, b) =>
        (orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
        (orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER)
    )
  }, [divisions, selectedDivisions, divisionPriorityOrder])

  const resetForm = useCallback(() => {
    setSelectedDivisions([])
    setSelectedLocations([])
    setConfirmStep(false)
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [onClose, resetForm])

  const handleConfirm = useCallback(() => {
    if (divisionsWithGames.length > 0 && !confirmStep) {
      setConfirmStep(true)
      return
    }

    // Pass division priority filtered to selected divisions, in priority order
    const fullOrder =
      divisionPriorityOrder.length > 0
        ? divisionPriorityOrder
        : divisions.map((d) => d.id)
    const priority = fullOrder.filter((id) => selectedDivisions.includes(id))
    onConfirm(selectedDivisions, selectedLocations, priority)
    handleClose()
  }, [
    onConfirm,
    selectedDivisions,
    selectedLocations,
    handleClose,
    divisionsWithGames,
    confirmStep,
    divisionPriorityOrder,
    divisions,
  ])

  const toggleDivisionSelection = useCallback(
    (divisionId: string, division: Division) => {
      if (division.isDisabled || division.isApproved) {
        return
      }

      setSelectedDivisions((prev) => {
        const isSelected = prev.includes(divisionId)
        if (isSelected) {
          // Deselect division and all its locations
          const divisionLocations =
            division.locations !== 'all'
              ? division.locations.map((loc) => loc.id)
              : []
          setSelectedLocations((prevLocs) =>
            prevLocs.filter((loc) => !divisionLocations.includes(loc))
          )
          return prev.filter((id) => id !== divisionId)
        } else {
          // Select division and all its locations
          if (division.locations !== 'all') {
            const locationIds = division.locations.map((loc) => loc.id)
            setSelectedLocations((prevLocs) => [
              ...new Set([...prevLocs, ...locationIds]),
            ])
          }
          return [...prev, divisionId]
        }
      })
    },
    []
  )

  const getLocationDisplayText = (division: Division): string => {
    if (division.locations === 'all') {
      return 'All Locations'
    }
    const count = division.locations.length
    return `${count} Location${count !== 1 ? 's' : ''}`
  }

  const isDivisionSelected = (divisionId: string): boolean => {
    return selectedDivisions.includes(divisionId)
  }

  const selectableDivisions = divisions.filter(
    (d) => !d.isDisabled && !d.isApproved
  )
  const allSelected =
    selectableDivisions.length > 0 &&
    selectableDivisions.every((d) => selectedDivisions.includes(d.id))

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      // Deselect all
      setSelectedDivisions([])
      setSelectedLocations([])
    } else {
      // Select all selectable divisions and their locations
      const ids = selectableDivisions.map((d) => d.id)
      const locIds = selectableDivisions.flatMap((d) =>
        d.locations !== 'all' ? d.locations.map((l) => l.id) : []
      )
      setSelectedDivisions(ids)
      setSelectedLocations(locIds)
    }
  }, [allSelected, selectableDivisions])

  const canConfirm = selectedDivisions.length > 0

  const handleCopyToClipboard = useCallback(() => {
    if (!previewPayload) {
      return
    }
    navigator.clipboard.writeText(JSON.stringify(previewPayload, null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [previewPayload])

  const showPreview = previewPayload !== null || isLoadingPreview

  return (
    <Modal
      css={modalStyles}
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={700}
      centered
      destroyOnClose
      maskClosable={false}
      styles={{
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
        },
      }}
    >
      {/* Header */}
      <div css={headerStyles}>
        <h2 css={titleStyles}>
          {showPreview
            ? 'Payload Preview'
            : confirmStep && !showPreview
            ? 'Confirm Regeneration'
            : 'Generate Schedule'}
        </h2>
        <button
          type='button'
          css={closeButtonStyles}
          onClick={handleClose}
          aria-label='Close modal'
        >
          <CloseOutlined />
        </button>
      </div>

      {showPreview ? (
        <>
          {/* Preview Content */}
          <div css={formStyles}>
            {isLoadingPreview ? (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 48px;
                  color: ${colors.NEUTRAL_50};
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                `}
              >
                Loading payload preview...
              </div>
            ) : (
              <pre
                css={css`
                  background-color: ${colors.SECONDARY_500};
                  color: ${colors.NEUTRAL_50};
                  font-family: 'Courier New', Courier, monospace;
                  font-size: 12px;
                  padding: 16px;
                  border-radius: 8px;
                  max-height: calc(90vh - 130px);
                  overflow-y: auto;
                  white-space: pre-wrap;
                  word-break: break-word;
                  margin: 0;
                `}
              >
                {JSON.stringify(previewPayload, null, 2)}
              </pre>
            )}
          </div>

          {/* Preview Footer */}
          <div css={footerStyles}>
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                width: 100%;
              `}
            >
              <button
                type='button'
                css={cancelButtonStyles}
                onClick={handleClose}
              >
                CLOSE
              </button>
              <button
                type='button'
                css={css`
                  ${confirmButtonStyles}
                  min-width: 180px;
                `}
                onClick={handleCopyToClipboard}
                disabled={isLoadingPreview}
              >
                {copied ? 'COPIED!' : 'COPY TO CLIPBOARD'}
              </button>
            </div>
          </div>
        </>
      ) : confirmStep ? (
        <>
          {/* Confirmation Content */}
          <div css={confirmationContainerStyles}>
            <div css={warningIconStyles}>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z'
                  fill={colors.WARNING_300}
                />
              </svg>
            </div>
            <p css={confirmationHeadingStyles}>
              This will replace existing games
            </p>
            <ul css={confirmationListStyles}>
              {divisionsWithGames.map((d) => (
                <li key={d.id} css={confirmationListItemStyles}>
                  {d.name}
                </li>
              ))}
            </ul>
            <p css={confirmationNoteStyles}>This action cannot be undone.</p>
          </div>

          {/* Confirmation Footer */}
          <div css={footerStyles}>
            <div css={buttonContainerStyles}>
              <button
                type='button'
                css={cancelButtonStyles}
                onClick={() => setConfirmStep(false)}
              >
                GO BACK
              </button>
              <button
                type='button'
                css={regenerateButtonStyles}
                onClick={() => {
                  const fullOrder =
                    divisionPriorityOrder.length > 0
                      ? divisionPriorityOrder
                      : divisions.map((d) => d.id)
                  const priority = fullOrder.filter((id) =>
                    selectedDivisions.includes(id)
                  )
                  onConfirm(selectedDivisions, selectedLocations, priority)
                  handleClose()
                }}
              >
                REGENERATE
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Form / Content */}
          <div css={formStyles}>
            {/* Section Header */}
            <div css={sectionHeaderStyles}>
              <p css={sectionTitleStyles}>Divisions</p>
              <p css={sectionSubtitleStyles}>Select divisions to generate</p>
              <p css={readyDisclaimerStyles}>
                <i className='fas fa-info-circle' /> Only divisions marked as
                &ldquo;Ready&rdquo; are shown here. To include more divisions,
                mark them as ready from the Divisions &amp; Teams tab.
              </p>
            </div>

            {/* Table */}
            <div css={tableContainerStyles}>
              {/* Table Header */}
              <div
                css={[
                  tableHeaderStyles,
                  css`
                    cursor: pointer;
                    &:hover {
                      background-color: ${colors.SECONDARY_200};
                    }
                  `,
                ]}
                onClick={toggleSelectAll}
                role='checkbox'
                aria-checked={allSelected}
                aria-label='Select all divisions'
              >
                <div css={checkboxContainerStyles}>
                  {allSelected ? (
                    <CheckboxCheckedIcon />
                  ) : (
                    <CheckboxOutlineIcon />
                  )}
                </div>
                <span css={tableHeaderCellStyles}>Divisions</span>
                <span css={tableHeaderCellStyles}>Locations</span>
                <span css={tableHeaderCellStyles}>Scheduling Status</span>
              </div>

              {/* Division Rows */}
              {orderedDivisions.map((division, index) => {
                const isSelected = isDivisionSelected(division.id)
                const isAltRow = index % 2 === 1

                return (
                  <div
                    key={division.id}
                    css={[
                      divisionRowStyles,
                      (division.isDisabled || division.isApproved) &&
                        divisionRowDisabledStyles,
                      isAltRow &&
                        !(division.isDisabled || division.isApproved) &&
                        !isSelected &&
                        altRowStyles,
                      isSelected && divisionRowSelectedStyles,
                    ]}
                    onClick={() => {
                      if (!division.isDisabled && !division.isApproved) {
                        toggleDivisionSelection(division.id, division)
                      }
                    }}
                    role='checkbox'
                    aria-checked={isSelected}
                    tabIndex={
                      division.isDisabled || division.isApproved ? -1 : 0
                    }
                    onKeyDown={(e) => {
                      if (
                        (e.key === 'Enter' || e.key === ' ') &&
                        !division.isDisabled &&
                        !division.isApproved
                      ) {
                        e.preventDefault()
                        toggleDivisionSelection(division.id, division)
                      }
                    }}
                  >
                    <div
                      css={[
                        checkboxContainerStyles,
                        isSelected && checkboxCheckedContainerStyles,
                      ]}
                    >
                      {division.isDisabled || division.isApproved ? (
                        <CheckboxDisabledIcon />
                      ) : isSelected ? (
                        <CheckboxCheckedIcon />
                      ) : (
                        <CheckboxOutlineIcon />
                      )}
                    </div>
                    <span css={divisionNameStyles}>{division.name}</span>
                    <span css={locationCellStyles}>
                      {getLocationDisplayText(division)}
                    </span>
                    <span css={statusCellStyles}>
                      {division.scheduleStatus || division.isApproved ? (
                        <StatusBadge
                          status={division.scheduleStatus}
                          isApproved={division.isApproved}
                        />
                      ) : (
                        division.status
                      )}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Footer */}
          <div css={footerStyles}>
            <div css={buttonContainerStyles}>
              <button
                type='button'
                css={cancelButtonStyles}
                onClick={handleClose}
              >
                CANCEL
              </button>
              <button
                type='button'
                css={confirmButtonStyles}
                onClick={handleConfirm}
                disabled={!canConfirm}
              >
                CONFIRM
              </button>
            </div>
          </div>
        </>
      )}
    </Modal>
  )
}

export default GenerateScheduleModal
